import axios from 'axios'
import i18n from '@/i18n'; // Importiere die i18n Instanz
import { API_URL as ENV_CONFIG_API_URL } from '@/utils/environmentConfig';
import { isDevelopment } from '@/config/api.config'; // isDevelopment wird weiterhin von hier bezogen

// API-URL für den Client
export const API_URL = ENV_CONFIG_API_URL; // Exportiere die korrekte API_URL

console.log('Verwende API-URL aus environmentConfig:', API_URL);

// API-Instanz mit Basis-URL und Standardeinstellungen erstellen
export const api = axios.create({
  baseURL: API_URL, // Dies ist /api (dev) oder https://api.slug.example.com (prod)
  timeout: 30000, // 30 Sekunden Timeout
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  // Verbesserte Fehlerbehandlung für Netzwerkprobleme
  validateStatus: function (status) {
    return status >= 200 && status < 500; // Default
  }
});


// Für Entwicklungszwecke: Logging der API-Anfragen
api.interceptors.request.use(
  config => {
    // Vermeidung doppelter API-Pfade
    if (config.url && config.url.includes('/api/api')) {
      config.url = config.url.replace('/api/api', '/api');
      console.log('Korrigierte URL mit doppeltem API-Pfad:', config.url);
    }
    
    // Hole das Token aus dem lokalen Speicher (korrigierter Schlüssel)
    const token = localStorage.getItem('auth_token');
    
    // Wenn ein Token existiert, füge es zu den Headers hinzu
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
      console.log('Authorization Header hinzugefügt für Request:', config.url);
    } else {
      console.warn('Kein Token gefunden für Request:', config.url);
    }

    // Multi-Tenancy: aktuelle Restaurant-ID an Backend mitschicken,
    // damit die ResolveRestaurant-Middleware nicht das Default-Restaurant (Tenant 1) lädt.
    if (!config.headers['X-Restaurant-Id']) {
      const rid = localStorage.getItem('current_restaurant_id');
      if (rid) config.headers['X-Restaurant-Id'] = rid;
    }

    return config;
  },
  error => {
    console.error('Fehler bei der API-Anfrage:', error);
    return Promise.reject(error);
  }
);

// Neuer Request-Interceptor für Accept-Language Header wird VOR dem Authorization Interceptor eingefügt
api.interceptors.request.use(config => {
  // Füge den Accept-Language Header hinzu, basierend auf der aktuellen i18n-Locale
  const currentLocale = i18n.global.locale?.value || 'de'; // Standard auf 'de', falls nicht verfügbar
  config.headers['Accept-Language'] = currentLocale;
  console.log(`Accept-Language Header gesetzt auf: ${currentLocale}`);
  return config;
}, error => {
  // Optional: Fehlerbehandlung für diesen spezifischen Interceptor
  console.error('Fehler beim Setzen des Accept-Language Headers:', error);
  return Promise.reject(error);
});

// Request Interceptor für einheitliche Fehlerbehandlung
api.interceptors.request.use(
  (config) => {
    // Zusätzliche Sicherheitsheader für HTTPS
    if (config.url && config.url.includes('https://')) {
      config.headers['Strict-Transport-Security'] = 'max-age=31536000; includeSubDomains';
    }
    return config;
  },
  (error) => {
    console.error('Fehler beim API-Request:', error);
    return Promise.reject(error);
  }
);

// Antwort-Interceptor für einheitliche Fehlerbehandlung
api.interceptors.response.use(
  response => {
    // Erfolgreiche Antwort
    return response;
  },
  error => {
    // Verbesserte Fehlerbehandlung
    if (error.message === 'Network Error') {
      console.warn('Netzwerkfehler: Backend nicht erreichbar. Server läuft wahrscheinlich nicht oder ist nicht zugänglich.');
      
      // Überprüfen, ob wir im Entwicklungsmodus sind
      if (isDevelopment) {
        console.info('Development-Modus: Stelle sicher, dass der Backend-Server unter der korrekten URL (standardmäßig https://localhost:5000, siehe Proxy-Konfiguration in vue.config.js) läuft und antwortet.');
      }
    } else if (error.response) {
      // Der Server hat mit einem Fehlercode geantwortet
      console.error(`API-Fehler ${error.response.status}:`, error.response.data);
      
      // Weitere spezifische Fehlerbehandlung je nach Statuscode
      if (error.response.status === 401) {
        // Unauthorized - Token abgelaufen oder ungültig
        console.warn('401 Unauthorized: Token ist abgelaufen oder ungültig. Führe automatisches Logout durch.');
        
        // Entferne ungültigen Token und Benutzerdaten
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        sessionStorage.removeItem('auth_token');
        sessionStorage.removeItem('user');
        
        // Leite zur Login-Seite weiter (nur wenn wir nicht bereits auf der Login-Seite sind)
        if (window.location.pathname !== '/login' && !window.location.pathname.includes('/login')) {
          console.log('Leite zur Login-Seite weiter aufgrund von 401 Unauthorized');
          window.location.href = '/login';
        }
      }
    } else if (error.request) {
      // Die Anfrage wurde gestellt, aber keine Antwort erhalten
      console.error('Keine Antwort vom Server:', error.request);
    } else {
      // Beim Erstellen der Anfrage ist ein Fehler aufgetreten
      console.error('Fehler beim Erstellen der API-Anfrage:', error.message);
    }
    
    return Promise.reject(error);
  }
);

// Export der API-Instanz und Backend-URL für Verwendung in anderen Dateien
export const instance = api;

// Authentifizierungs-Service
export const authService = {
  login: async (credentials) => {
    try {
      const response = await api.post('/Auth/login', credentials);
      
      // Token speichern, falls in der Antwort vorhanden
      if (response.data && response.data.token) {
        localStorage.setItem('auth_token', response.data.token);
        console.log('Anmeldung erfolgreich, Token gespeichert');
      }
      
      return response;
    } catch (error) {
      console.error('Login-Fehler:', error);
      throw error; // Fehler weitergeben zur Behandlung in der Komponente
    }
  },
  
  logout: () => {
    try {
      // Token und Benutzerdaten vollständig entfernen
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      sessionStorage.removeItem('auth_token');
      sessionStorage.removeItem('user');
      
      // Alle anderen relevanten Daten löschen
      localStorage.removeItem('cart');
      localStorage.removeItem('tableId');
      localStorage.removeItem('sessionId');
      
      console.log('Abmeldung erfolgreich, alle Benutzerdaten entfernt');
      
      // Alle Header zurücksetzen
      delete api.defaults.headers.common['Authorization'];
      
      // Optional: Abmelde-Aufruf an den Server
      return api.post('/Auth/logout').catch(error => {
        console.warn('Fehler beim Server-Logout, lokale Abmeldung erfolgt:', error);
        return Promise.resolve({ data: { success: true } });
      });
    } catch (error) {
      console.error('Fehler bei der Abmeldung:', error);
      return Promise.resolve({ data: { success: true } });
    }
  },
  
  getUser: () => {
    return api.get('/Auth/user');
  },
  
  isAuthenticated: () => {
    return !!localStorage.getItem('auth_token');
  }
};

// Kunden-Service für Bestellungen und Menüs
export const customerService = {
  getTableInfo: (tableId) => {
    return api.get(`/Tables/${tableId}`)
      .catch(error => {
        console.error('Fehler beim Laden der Tischinformationen:', error);
        return { data: { name: `Tisch ${tableId}`, isAvailable: true, restaurantName: 'Restaurant' } };
      });
  },
  getSession: (sessionId) => {
    return api.get(`/OrderSessions/identifier/${sessionId}`)
      .catch(error => {
        console.error('Fehler beim Laden der Session:', error);
        return { data: null };
      });
  },
  getMenu: () => {
    return api.get('/Products')
      .then(response => {
        // Produkte gruppiert nach Kategorien zurückgeben
        return api.get('/Categories')
          .then(categoryResponse => {
            // Stelle sicher, dass wir gültige Arrays haben
            const categories = categoryResponse.data || [];
            const products = response.data || [];
            
            // Kategorien mit den zugehörigen Produkten anreichern
            const menuCategories = categories.map(category => {
              return {
                ...category,
                products: products.filter(product => product.categoryId === category.id)
              };
            });
            
            return { data: menuCategories };
          })
          .catch(error => {
            console.error('Fehler beim Laden der Kategorien:', error);
            return { data: [] }; // Leeres Array zurückgeben bei Fehler
          });
      })
      .catch(error => {
        console.error('Fehler beim Laden der Produkte:', error);
        return { data: [] }; // Leeres Array zurückgeben bei Fehler
      });
  },
  getProductDetails: (productId) => {
    return api.get(`/Products/${productId}`)
      .then(response => {
        console.log('API-Antwort für Produkt:', response.data);
        // Keine weitere Verarbeitung der availableExtras notwendig, 
        // da sie jetzt direkt als vollständige Objekte vom Backend kommen
        return response;
      })
      .catch(error => {
        console.error('Fehler beim Laden der Produktdetails:', error);
        return { data: null };
      });
  },
  getProductExtras: (productId) => {
    // Diese Methode wird nicht mehr verwendet, aber zur Kompatibilität beibehalten
    console.log("getProductExtras aufgerufen für Produkt:", productId);
    console.log("INFO: Diese Methode wird nicht mehr verwendet, da die Extras jetzt direkt in der Produktantwort enthalten sind.");
    
    return api.get(`/Products/${productId}`)
      .then(response => {
        const product = response.data;
        if (!product || !product.availableExtras || !Array.isArray(product.availableExtras)) {
          return { data: [] };
        }
        
        // Wir geben die Extras direkt zurück, da sie jetzt vollständige Objekte sind
        return { data: product.availableExtras };
      })
      .catch(error => {
        console.error(`Fehler beim Laden der Produktextras:`, error);
        return { data: [] };
      });
  },
  initSession: (sessionData) => {
    // Füge einen Zeitstempel und eine zufällige ID hinzu, um sicherzustellen, dass jede Session eindeutig ist
    const uniqueSessionData = {
      ...sessionData,
      timestamp: new Date().getTime(),
      uniqueId: Math.random().toString(36).substring(2, 15)
    };
    return api.post('/OrderSessions', uniqueSessionData);
  },
  createOrder: (orderData) => {
    return api.post('/Orders', orderData);
  },
  getOrderStatus: (orderId) => {
    return api.get(`/Orders/${orderId}`);
  },
  // Methode zum Abrufen der Tischdetails für die moderne UI
  getTableDetails: (tableId) => {
    return api.get(`/Tables/${tableId}/details`)
      .catch(error => {
        console.error('Fehler beim Laden der Tischdetails:', error);
        return { 
          data: { 
            id: tableId, 
            name: `Tisch ${tableId}`, 
            isAvailable: true, 
            restaurantId: 1,
            restaurantName: 'Restaurant',
            qrCodeUrl: '' 
          } 
        };
      });
  },
  
  // Moderne Menü-Schnittstelle (OPTIMIERT)
  getModernMenu: () => {
    console.log('Rufe optimiertes modernes Menü ab...');
    // Direkter Aufruf des neuen Backend-Endpunkts, der Kategorien mit Produkten zurückgibt
    return api.get('/Categories/WithProducts')
      .then(response => {
        console.log('Optimiertes Menü erfolgreich geladen:', response.data);
        // Füge Standard-Bild-URLs hinzu, falls im Backend keine vorhanden sind
        // (Obwohl es besser wäre, wenn das Backend immer eine URL liefert)
        const categoriesWithImages = (response.data || []).map(category => ({
          ...category,
          imageUrl: category.imageUrl || `/img/categories/category-${category.id}.jpg`,
          products: (category.products || []).map(product => ({
            ...product,
            imageUrl: product.imageUrl || `/img/products/product-${product.id}.jpg`
          }))
        }));
        return { data: categoriesWithImages };
      })
      .catch(error => {
        console.error('Fehler beim Laden des optimierten modernen Menüs:', error);
        // Bei Fehlern leeres Array zurückgeben, um die UI nicht zu blockieren
        return { data: [] }; 
      });
  },
  
  // Moderne Produktdetails mit Optionen (OPTIMIERT)
  getModernProductDetails: (productId) => {
    console.log('Rufe optimierte moderne Produktdetails ab für Produkt ID:', productId);
    // Direkter Aufruf des Endpunkts, der Produkt MIT Extras zurückgibt
    return api.get(`/Products/${productId}`)
      .then(response => {
        const product = response.data;
        console.log('Optimierte Produktdetails erfolgreich geladen:', product);
        
        // Füge Standard-Bild-URL hinzu, falls im Backend keine vorhanden ist
        if (product && !product.imageUrl) {
          product.imageUrl = `/img/products/product-${product.id}.jpg`;
        }
        
        // Die Extra-Optionen sind bereits in der erwarteten Struktur (gruppiert) 
        // durch den ProductService im Backend aufbereitet.
        // Es sind keine weiteren Frontend-Aufrufe oder Transformationen für Extras nötig.
        
        // Optional: Füge Bilder zu den Extra-Optionen hinzu, falls nicht vom Backend geliefert
        if (product && product.availableExtras && Array.isArray(product.availableExtras)) {
          product.availableExtras.forEach(group => {
            if (group.options && Array.isArray(group.options)) {
              group.options.forEach(option => {
                if (!option.imageUrl) {
                  option.imageUrl = `/img/options/option-${option.id}.jpg`;
                }
              });
            }
          });
        }
        
        return { data: product };
      })
      .catch(error => {
        console.error('Fehler beim Laden der optimierten modernen Produktdetails:', error);
        return { data: null };
      });
  },
  
  // Moderne Session-Initialisierung
  initModernSession: (tableId, customerData) => {
    const sessionData = {
      tableId,
      customerName: customerData.name,
      customerEmail: customerData.email,
      // Einzigartige Session-ID generieren
      uniqueIdentifier: `${tableId}-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`
    };
    
    console.log('Initialisiere moderne Session:', sessionData);
    return api.post('/OrderSessions', sessionData);
  },
  
  // Warenkorb für moderne UI
  getModernCart: (sessionId) => {
    return api.get(`/OrderSessions/${sessionId}/cart`)
      .catch(error => {
        console.error('Fehler beim Laden des Warenkorbs:', error);
        return { data: { items: [], totalAmount: 0 } };
      });
  },
  
  addToModernCart: (sessionId, item) => {
    return api.post(`/OrderSessions/${sessionId}/cart`, item);
  },
  
  updateModernCartItem: (sessionId, itemId, quantity) => {
    return api.put(`/OrderSessions/${sessionId}/cart/${itemId}`, { quantity });
  },
  
  removeFromModernCart: (sessionId, itemId) => {
    return api.delete(`/OrderSessions/${sessionId}/cart/${itemId}`);
  },
  
  // Moderne Bestellung aufgeben
  createModernOrder: (sessionId, orderData) => {
    return api.post(`/OrderSessions/${sessionId}/orders`, orderData);
  }
}

// Admin-Service für Verwaltungsfunktionen
export const adminService = {
  // Dashboard
  getDashboardStats: () => {
    return api.get('/Dashboard/stats')
  },
  
  getRecentOrders: (count = 5) => {
    return api.get(`/Dashboard/recent-orders`, { params: { count } })
  },
  
  // Bestellungen
  getOrders: (filters = {}) => {
    console.log('API-Aufruf: getOrders mit Filtern:', filters);
    
    // Vereinfachte Anfrage mit Fehlerbehandlung
    return api.get('/Orders', { params: filters })
      .then(response => {
        console.log('Erhaltene Bestellungen:', response.data);
        return response;
      })
      .catch(error => {
        console.error('Fehler beim Laden der Bestellungen, versuche alternative URL:', error);
        
        // Versuche alternative URL mit /api Präfix
        return api.get('/Orders', { params: filters })
          .then(response => {
            console.log('Bestellungen von alternativer URL erhalten:', response.data);
            return response;
          })
          .catch(finalError => {
            console.error('Alle Versuche, Bestellungen zu laden, fehlgeschlagen:', finalError);
            return { data: [] }; // Leeres Array zurückgeben, wenn alles fehlschlägt
          });
      });
  },
  getOrderDetails: (orderId) => {
    return api.get(`/Orders/${orderId}`)
  },
  updateOrderStatus: (orderId, status) => {
    console.log(`API-Aufruf: updateOrderStatus für Order ${orderId} zu Status ${status}`);
    
    // Standard-Anfrage-Konfiguration
    const config = {
      headers: { 'Content-Type': 'application/json' }
    };
    
    return api.patch(`/Orders/${orderId}/status`, JSON.stringify(status), config)
      .catch(error => {
        console.error('Fehler beim Aktualisieren des Bestellstatus, versuche alternative URL:', error);
        // Versuche alternative URL
        return api.patch(`/Orders/${orderId}/status`, JSON.stringify(status), config);
      });
  },
  updateOrderPaymentStatus: (orderId, paymentStatus) => {
    console.log(`API-Aufruf: updateOrderPaymentStatus für Order ${orderId} zu Status ${paymentStatus}`);
    
    // Standard-Anfrage-Konfiguration
    const config = {
      headers: { 'Content-Type': 'application/json' }
    };
    
    return api.patch(`/Orders/${orderId}/payment-status`, JSON.stringify(paymentStatus), config)
      .catch(error => {
        console.error('Fehler beim Aktualisieren des Zahlungsstatus, versuche alternative URL:', error);
        // Versuche alternative URL
        return api.patch(`/Orders/${orderId}/payment-status`, JSON.stringify(paymentStatus), config);
      });
  },
  
  // Produkte
  getProducts: (filters = {}) => {
    return api.get('/Products', { params: filters })
  },
  getProduct: (productId) => {
    return api.get(`/Products/${productId}`)
      .then(response => {
        // Prüfen, ob Daten zurückgegeben wurden
        if (!response.data) {
          return { data: null };
        }
        
        // Wir stellen sicher, dass availableExtras ein Array von IDs ist und keine Objekte
        if (response.data.availableExtras) {
          // Wenn availableExtras ein Array von Objekten ist, extrahieren wir nur die IDs
          if (Array.isArray(response.data.availableExtras) && response.data.availableExtras.length > 0 
              && typeof response.data.availableExtras[0] === 'object') {
            response.data.availableExtras = response.data.availableExtras.map(extra => extra.id);
          }
          
          // Sicherstellen, dass alle IDs als Zahlen vorliegen
          response.data.availableExtras = response.data.availableExtras.map(id => 
            typeof id === 'string' ? parseInt(id) : id
          );
        } else {
          // Falls keine Extras vorhanden sind, initialisieren wir mit einem leeren Array
          response.data.availableExtras = [];
        }
        
        return response;
      })
      .catch(error => {
        console.error('Fehler beim Laden des Produkts:', error);
        return { data: { availableExtras: [] } };
      });
  },
  createProduct: (product) => {
    return api.post('/Products', product)
  },
  updateProduct: (productId, product) => {
    // Stelle sicher, dass availableExtras ein gültiges Array ist
    if (!product.availableExtras) {
      product.availableExtras = [];
    }

    // Stelle sicher, dass die richtigen Typen gesendet werden
    const cleanedProduct = {
      ...product,
      price: Number(product.price),
      categoryId: Number(product.categoryId),
      availableExtras: Array.isArray(product.availableExtras) 
        ? product.availableExtras.map(id => Number(id))
        : []
    };

    return api.put(`/Products/${productId}`, cleanedProduct);
  },
  deleteProduct: (productId) => {
    return api.delete(`/Products/${productId}`)
  },
  updateProductPrice: (productId, price) => {
    return api.patch(`/Products/${productId}/price`, { price })
  },
  
  // Kategorien
  getCategories: () => {
    return api.get('/Categories')
  },
  createCategory: (category) => {
    return api.post('/Categories', category)
  },
  updateCategory: (categoryId, category) => {
    return api.put(`/Categories/${categoryId}`, category)
  },
  deleteCategory: (categoryId) => {
    return api.delete(`/Categories/${categoryId}`)
  },
  
  // Extras
  getExtras: () => {
    return api.get('/Extras')
  },
  createExtra: (extraData) => {
    // Extrahieren des ExtraGroupDto aus dem übergebenen Objekt
    const extraGroupDto = extraData.extraGroupDto;
    console.log('Senden von Extra-Daten (POST):', extraGroupDto);
    return api.post('/Extras', extraGroupDto);
  },
  updateExtra: (extraId, extraData) => {
    // Extrahieren des ExtraGroupDto aus dem übergebenen Objekt
    const extraGroupDto = extraData.extraGroupDto;
    console.log('Senden von Extra-Daten (PUT):', extraId, extraGroupDto);
    return api.put(`/Extras/${extraId}`, extraGroupDto);
  },
  createExtraOption: (option) => {
    return api.post('/Extras/options', option)
  },
  updateExtraOption: (optionId, option) => {
    return api.put(`/Extras/options/${optionId}`, option)
  },
  getExtraOptionsByGroupId: (groupId) => {
    console.log(`API-Aufruf: getExtraOptionsByGroupId für Gruppe ${groupId}`);
    return api.get(`/Extras/GroupOptions/${groupId}`)
      .then(response => {
        console.log(`Erhaltene Optionen für Gruppe ${groupId}:`, response.data);
        return response;
      })
      .catch(error => {
        console.error(`Fehler beim Laden der Optionen für Gruppe ${groupId}:`, error);
        return { data: [] };
      });
  },
  deleteExtra: (extraId) => {
    return api.delete(`/Extras/${extraId}`)
  },
  deleteExtraOption: (optionId) => {
    return api.delete(`/Extras/options/${optionId}`)
  },
  
  // Tische
  getTables: (filters = {}) => {
    return api.get('/Tables', { params: filters })
  },
  getTable: (tableId) => {
    return api.get(`/Tables/${tableId}`)
  },
  getOrdersForTable: (tableId, params = {}) => {
    return api.get(`/orders/table/${tableId}`, { params })
  },
  createTable: (table) => {
    return api.post('/Tables', table)
  },
  updateTable: (tableId, table) => {
    return api.put(`/Tables/${tableId}`, table)
  },
  updateTableStatus: (tableId, status) => {
    return api.patch(`/Tables/${tableId}/availability`, status)
  },
  deleteTable: (tableId) => {
    return api.delete(`/Tables/${tableId}`)
  },
  
  // Benutzerverwaltung
  getUsers: (filters = {}) => {
    return api.get('/Users', { params: filters })
  },
  getUser: (userId) => {
    return api.get(`/Users/${userId}`)
  },
  createUser: (user) => {
    return api.post('/Users', user)
  },
  updateUser: (userId, user) => {
    return api.put(`/Users/${userId}`, user)
  },
  deleteUser: (userId) => {
    return api.delete(`/Users/${userId}`)
  },
  
  // Berichte
  generateReport: (type, timePeriod, startDate, endDate) => {
    return api.get('/Reports', {
      params: { type, timePeriod, startDate, endDate }
    })
  },
  exportReport: (type, timePeriod, startDate, endDate, format = 'pdf') => {
    return api.get('/Reports/export', {
      params: { type, timePeriod, startDate, endDate, format },
      responseType: 'blob'
    })
  },
  createTableSession: (sessionData) => {
    // Füge einen Zeitstempel und eine zufällige ID hinzu, um sicherzustellen, dass jede Session eindeutig ist
    const uniqueSessionData = {
      ...sessionData,
      timestamp: new Date().getTime(),
      uniqueId: Math.random().toString(36).substring(2, 15)
    };
    return api.post('/Tables/sessions', uniqueSessionData);
  },
  
  // Produktverwaltung
  uploadProductImage: (productId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    return api.post(`/Products/${productId}/image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }
}

// SuperAdmin-Service für Restaurant-Verwaltung
export const superAdminService = {
  // Dashboard-Daten
  getDashboardStats: () => {
    console.log('Rufe SuperAdmin Dashboard Stats auf');
    return api.get('/SuperAdmin/dashboard/stats');
  },
  
  // Restaurant-Verwaltung
  getAllRestaurants: () => {
    return api.get('/SuperAdmin/restaurants');
  },
  getRestaurantById: (id) => {
    return api.get(`/SuperAdmin/restaurants/${id}`);
  },
  createRestaurant: (restaurant) => {
    return api.post('/SuperAdmin/restaurants', restaurant);
  },
  updateRestaurant: (id, restaurant) => {
    return api.put(`/SuperAdmin/restaurants/${id}`, restaurant);
  },
  toggleRestaurantStatus: (id, isActive) => {
     // Sende als Objekt im Body, wie im Controller erwartet
    return api.put(`/SuperAdmin/restaurants/${id}/status`, { isActive }); 
  },
  deleteRestaurant: (id) => {
    return api.delete(`/SuperAdmin/restaurants/${id}`);
  }
  // TODO: Methoden für Logs, Admin-Zuweisung etc.
};

// Das gesamte API-Serviceobjekt
const apiService = {
  auth: authService,
  customer: customerService,
  admin: adminService,
  superAdmin: superAdminService
}

// Einziger default export der Datei
export default apiService

// ─── Hotel-Edition: Re-Exports aus dem dedizierten Modul ────────────────
// (Damit `import { hotelStatsService } from '@/services/api'` immer funktioniert,
//  unabhängig davon, ob Webpack diese Datei oder den Ordner-Index auflöst.)
export {
  locationService,
  roomService,
  roomTokenService,
  preOrderService,
  hotelStatsService,
  hotelGroupService,
  onboardingService,
  voucherService,
  splitBillService,
  bulkService,
  waiterService,
} from './api/hotelService'

// ─── Weitere Services (Re-Exports, damit Imports aus '@/services/api' funktionieren) ───
export { roomChargeService } from './api/roomChargeService'
export { orderService } from './api/orderService'
export { productsService } from './api/productsService'
export { sessionService } from './api/sessionService'
export { paymentService } from './api/paymentService'
export { restaurantSettingsService } from './api/restaurantSettingsService'
export { cartService } from './api/cartService'
export { dashboardService } from './api/dashboardService'
export { printerService } from './api/printerService'
export { extraOptionService } from './api/extraOptionService'
export { userService } from './api/userService'

// Produkt-Endpunkte
export async function getProducts() {
  return await api.get('api/products');
}

export async function getProductById(productId) {
  return await api.get(`api/products/${productId}`);
}

export async function getProductsByCategory(categoryId) {
  return await api.get(`api/products/category/${categoryId}`);
}

export async function getProductsByRestaurant(restaurantId) {
  return await api.get(`api/products/restaurant/${restaurantId}`);
}

// Neue Endpunkte für Combo-Produkte und Getränke
export async function getComboProducts(restaurantId) {
    return await api.get(`api/products/combos/${restaurantId}`);
}

export async function getBeverageProducts(restaurantId) {
  return await api.get(`api/products/beverages/${restaurantId}`);
}

export async function getSideDishProducts(restaurantId) {
  return await api.get(`api/products/sides/${restaurantId}`);
}

export async function getBeverageSuggestions(restaurantId, productIds) {
  // Erstelle eine Query-String für die Produkt-IDs
  const queryParams = productIds.map(id => `productIds=${id}`).join('&');
  return await api.get(`api/products/beverage-suggestions/${restaurantId}?${queryParams}`);
} 

export async function getRestaurantSettings() {
  return await api.get('/RestaurantSettings');
}

export async function updateRestaurantSettings(settings) {
  return await api.put('/RestaurantSettings', settings);
}

export async function uploadRestaurantLogo(formData) {
  return await api.post('/RestaurantSettings/logo', formData);
}

export async function changeRestaurantPassword(passwordData) {
  return await api.post('/Users/change-password', passwordData);
}

export async function updateRestaurantPayment(paymentData) {
  return await api.put('/RestaurantSettings/payment', paymentData);
}
